import { Dispatch, SetStateAction, useEffect, useMemo, useState } from "react"
import { Dayjs } from "utils/dayjs"
import { Guid } from "@/types"
import useReservation from "hooks/useReservation"
import Reservation from "models/Reservation"
import { useTenantSettings } from "stores/tenantSettings"
import UserService from "services/UserService"

interface IUseAssetInitialSaleOrRentalProps {
    isVisible: boolean
}

export interface IUseAssetInitialSaleOrRental {
    relevantForAssetStatistic: boolean
    setRelevantForAssetStatistic: Dispatch<SetStateAction<boolean>>
    reportAsset: boolean
    setReportAsset: Dispatch<SetStateAction<boolean>>
    reservationId: Guid | null
    setReservationId: Dispatch<SetStateAction<Guid | null>>
    changedByUserEmail: string | null
    setChangeUserId: Dispatch<SetStateAction<Guid | null>>
    changeDate: Dayjs | null
    setChangeDate: Dispatch<SetStateAction<Dayjs | null>>
    locked: boolean
    setLocked: Dispatch<SetStateAction<boolean>>
    reservation: Reservation | null
    salesPersonName?: string
    isReservationIdRequired: boolean
    isVisible: boolean
}

export default function useAssetInitialSaleOrRental({
    isVisible,
}: IUseAssetInitialSaleOrRentalProps): IUseAssetInitialSaleOrRental {
    const [relevantForAssetStatistic, setRelevantForAssetStatistic] = useState(false)
    const [reportAsset, setReportAsset] = useState(false)
    const [reservationId, setReservationId] = useState<string | null>(null)
    const [changeUserId, setChangeUserId] = useState<string | null>(null)
    const [changedByUserEmail, setChangedByUserEmail] = useState<string | null>(null)
    const [changeDate, setChangeDate] = useState<Dayjs | null>(null)
    const [locked, setLocked] = useState(false)
    const isReservationIdRequired = relevantForAssetStatistic && reportAsset

    const { reservation } = useReservation({ reservationId })

    const {
        tenantEmployees,
        fetchTenantEmployees,
    } = useTenantSettings()

    const salesPersonName = useMemo(() => {
        if (!isVisible || !tenantEmployees || !reservation?.salesAdminNumber) return
        return tenantEmployees?.entities.find(employee => employee.number === reservation.salesAdminNumber)?.fullName
    }, [isVisible, reservation?.salesAdminNumber, tenantEmployees])

    useEffect(() => {
        if (!isVisible || reservation?.salesAdminNumber) return
        fetchTenantEmployees()
    }, [fetchTenantEmployees, isVisible, reservation?.salesAdminNumber])

    useEffect(() => {
        (async () => {
            if (!isVisible || !changeUserId) return
            const emails = await UserService.getUserEmailsByIds([changeUserId])
            setChangedByUserEmail(emails[changeUserId] || null)
        })()
    }, [changeUserId, isVisible])


    return {
        relevantForAssetStatistic,
        setRelevantForAssetStatistic,
        reportAsset,
        setReportAsset,
        reservationId,
        setReservationId,
        changedByUserEmail,
        setChangeUserId,
        changeDate,
        setChangeDate,
        locked,
        setLocked,
        reservation,
        salesPersonName,
        isReservationIdRequired,
        isVisible,
    }
}
